import { useInitialLoading } from '../hooks/useInitialLoading';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuthHeaders } from '../utils/authHeaders';

const API = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function OutsourcingAssignments() {
  const navigate = useNavigate();
  const companyId = localStorage.getItem('selectedCompanyId');
  const [assignments, setAssignments] = useState([]);
  const [loading, setLoading] = useState(true);
  const initialLoading = useInitialLoading(loading);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [clientFilter, setClientFilter] = useState('');
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  const [selected, setSelected] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    outsourcing_client_id:'', employee_id:'', outsourcing_service_id:'', status:'draft',
    start_date:'', end_date:'', outsourcing_salary:'', billing_rate:'',
    transportation_type:'none', transportation_amount:'', insurance_type:'none', insurance_amount:'', notes:''
  });

  const [clients, setClients] = useState([]);
  const [clientServicesMap, setClientServicesMap] = useState([]);

  useEffect(() => { if (companyId) { fetchAssignments(); loadClients(); } }, [companyId, page, statusFilter, clientFilter]);

  const authFetch = async (url, opts = {}) => {
    const res = await fetch(url, { ...opts, headers: { ...getAuthHeaders(), ...(opts.headers || {}) } });
    if (res.status === 401 || res.status === 403) { navigate('/login'); return null; }
    return res;
  };

  const fetchAssignments = async () => {
    setLoading(true);
    let url = `${API}/companies/${companyId}/outsourcing/assignments?page=${page}`;
    if (search) url += `&search=${encodeURIComponent(search)}`;
    if (statusFilter) url += `&status=${statusFilter}`;
    if (clientFilter) url += `&client_id=${clientFilter}`;
    const res = await authFetch(url);
    if (!res) return;
    const json = await res.json();
    setAssignments(json.data || []);
    setLastPage(json.last_page || 1);
    setLoading(false);
  };

  const loadClients = async () => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients?status=active`);
    if (res) { const j = await res.json(); setClients(j.data || []); }
  };

  const loadClientServices = async (clientId) => {
    if (!clientId) { setClientServicesMap([]); return; }
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/clients/${clientId}/services`);
    if (res) setClientServicesMap(await res.json());
  };

  const openAssignment = async (a) => {
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/assignments/${a.id}`);
    if (!res) return;
    setSelected(await res.json());
  };

  const saveAssignment = async () => {
    const payload = { ...form };
    if (selected) {
      delete payload.outsourcing_client_id;
      delete payload.employee_id;
    }
    if (!payload.outsourcing_service_id) payload.outsourcing_service_id = null;
    if (!payload.end_date) payload.end_date = null;
    if (!payload.transportation_amount) payload.transportation_amount = 0;
    if (!payload.insurance_amount) payload.insurance_amount = 0;

    const method = selected ? 'PUT' : 'POST';
    const url = selected ? `${API}/companies/${companyId}/outsourcing/assignments/${selected.id}` : `${API}/companies/${companyId}/outsourcing/assignments`;
    const res = await authFetch(url, { method, headers:{'Content-Type':'application/json'}, body: JSON.stringify(payload) });
    if (!res) return;
    if (res.ok) { setShowForm(false); setSelected(null); fetchAssignments(); }
    else { const e = await res.json(); window.alert(e.message || 'Error'); }
  };

  const changeStatus = async (id, newStatus) => {
    if (!window.confirm(`Change status to "${newStatus}"?`)) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/assignments/${id}/status`, { method:'POST', headers:{'Content-Type':'application/json'}, body: JSON.stringify({status:newStatus}) });
    if (res && res.ok) { const fresh = await res.json(); setSelected(fresh); fetchAssignments(); }
  };

  const archiveAssignment = async (id) => {
    if (!window.confirm('Archive this assignment?')) return;
    const res = await authFetch(`${API}/companies/${companyId}/outsourcing/assignments/${id}/archive`, { method:'POST' });
    if (res && res.ok) { setSelected(null); fetchAssignments(); }
  };

  const openCreateForm = () => {
    setForm({
      outsourcing_client_id:'', employee_id:'', outsourcing_service_id:'', status:'draft',
      start_date:'', end_date:'', outsourcing_salary:'', billing_rate:'',
      transportation_type:'none', transportation_amount:'', insurance_type:'none', insurance_amount:'', notes:''
    });
    setSelected(null);
    setClientServicesMap([]);
    setShowForm(true);
  };

  const openEditForm = () => {
    setForm({
      outsourcing_client_id: selected.outsourcing_client_id, employee_id: selected.employee_id,
      outsourcing_service_id: selected.outsourcing_service_id || '', status: selected.status,
      start_date: selected.start_date || '', end_date: selected.end_date || '',
      outsourcing_salary: selected.outsourcing_salary ?? '', billing_rate: selected.billing_rate ?? '',
      transportation_type: selected.transportation_type || 'none', transportation_amount: selected.transportation_amount || '',
      insurance_type: selected.insurance_type || 'none', insurance_amount: selected.insurance_amount || '', notes: selected.notes || ''
    });
    loadClientServices(selected.outsourcing_client_id);
    setShowForm(true);
  };

  if (!companyId) return <div style={st.page}><p>Please select a company first.</p></div>;

  // ── DETAIL VIEW ──
  if (selected && !showForm) {
    const a = selected;
    return (
      <div style={st.page}>
        <button onClick={() => setSelected(null)} style={st.backBtn}>← Back to Assignments</button>
        <div style={st.detailHeader}>
          <h2 style={st.title}>{a.employee?.name || `Employee #${a.employee_id}`}</h2>
          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
            <span style={{...st.badge, backgroundColor:statusClr(a.status)}}>{a.status}</span>
            {a.status !== 'archived' && <button onClick={openEditForm} style={st.btnPrimary}>Edit</button>}
            {a.status !== 'archived' && <button onClick={() => archiveAssignment(a.id)} style={st.btnWarn}>Archive</button>}
          </div>
        </div>

        <div style={st.section}>
          <div style={st.grid2}>
            <div><strong>Client:</strong> {a.client?.name || '—'} ({a.client?.code || ''})</div>
            <div><strong>Employee:</strong> {a.employee?.name || '—'} ({a.employee?.employee_code || ''})</div>
            <div><strong>Service:</strong> {a.service?.name || '—'} ({a.service?.code || ''})</div>
            <div><strong>Department:</strong> {a.employee?.department?.name || '—'}</div>
            <div><strong>Start Date:</strong> {a.start_date || '—'}</div>
            <div><strong>End Date:</strong> {a.end_date || '—'}</div>
          </div>
        </div>

        <div style={st.section}>
          <h3 style={{marginTop:0,marginBottom:12}}>Financial Details</h3>
          <div style={st.grid2}>
            <div><strong>Outsourcing Salary:</strong> ${Number(a.outsourcing_salary||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div>
            <div><strong>Billing Rate:</strong> ${Number(a.billing_rate||0).toLocaleString('en-US',{minimumFractionDigits:2})}</div>
            <div><strong>Transportation:</strong> {a.transportation_type} {a.transportation_amount ? `— $${a.transportation_amount}` : ''}</div>
            <div><strong>Insurance:</strong> {a.insurance_type} {a.insurance_amount ? `— $${a.insurance_amount}` : ''}</div>
          </div>
        </div>

        {/* Quick Status Change */}
        {a.status !== 'archived' && (
          <div style={st.section}>
            <h3 style={{marginTop:0,marginBottom:12}}>Change Status</h3>
            <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
              {['draft','active','on_hold','ended'].filter(s => s !== a.status).map(s => (
                <button key={s} onClick={() => changeStatus(a.id, s)} style={{...st.btnSm, background: statusClr(s), color:'#fff'}}>{s}</button>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  }

  // ── FORM MODAL ──
  const formModal = showForm ? (
    <div style={st.overlay} onClick={() => setShowForm(false)}>
      <div style={st.modal} onClick={e => e.stopPropagation()}>
        <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:16}}>
          <h3 style={{margin:0}}>{selected ? 'Edit Assignment' : 'New Assignment'}</h3>
          <button onClick={() => setShowForm(false)} style={{background:'none',border:'none',fontSize:20,cursor:'pointer'}}>✕</button>
        </div>

        <label style={st.label}>Client *</label>
        <select aria-label="Client" disabled={Boolean(selected)} value={form.outsourcing_client_id} onChange={e => { setForm({...form,outsourcing_client_id:e.target.value,outsourcing_service_id:''}); loadClientServices(e.target.value); }} style={st.input}>
          <option value="">Select Client...</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name} ({c.code})</option>)}
        </select>

        <label style={st.label}>Employee ID *</label>
        <input aria-label="Employee ID" disabled={Boolean(selected)} type="number" value={form.employee_id} onChange={e => setForm({...form,employee_id:e.target.value})} style={st.input} />

        <label style={st.label}>Service</label>
        <select aria-label="Service" value={form.outsourcing_service_id} onChange={e => setForm({...form,outsourcing_service_id:e.target.value})} style={st.input}>
          <option value="">None</option>
          {clientServicesMap.filter(cs => cs.status === 'active').map(cs => (
            <option key={cs.id} value={cs.outsourcing_service_id}>{cs.service?.name || '—'} ({cs.service?.code || ''})</option>
          ))}
        </select>

        <div style={st.grid2}>
          <div>
            <label style={st.label}>Start Date *</label>
            <input type="date" value={form.start_date} onChange={e => setForm({...form,start_date:e.target.value})} style={st.input} />
          </div>
          <div>
            <label style={st.label}>End Date</label>
            <input aria-label="End Date" type="date" value={form.end_date} onChange={e => setForm({...form,end_date:e.target.value})} style={st.input} />
          </div>
        </div>

        <div style={st.grid2}>
          <div>
            <label style={st.label}>Outsourcing Salary *</label>
            <input type="number" step="0.01" value={form.outsourcing_salary} onChange={e => setForm({...form,outsourcing_salary:e.target.value})} style={st.input} />
          </div>
          <div>
            <label style={st.label}>Billing Rate *</label>
            <input type="number" step="0.01" value={form.billing_rate} onChange={e => setForm({...form,billing_rate:e.target.value})} style={st.input} />
          </div>
        </div>

        <div style={st.grid2}>
          <div>
            <label style={st.label}>Transportation Type</label>
            <select value={form.transportation_type} onChange={e => setForm({...form,transportation_type:e.target.value})} style={st.input}>
              <option value="none">None</option>
              <option value="fixed">Fixed</option>
              <option value="attendance_based">Attendance-Based</option>
            </select>
          </div>
          <div>
            <label style={st.label}>Transportation Amount</label>
            <input type="number" step="0.01" value={form.transportation_amount} onChange={e => setForm({...form,transportation_amount:e.target.value})} style={st.input} disabled={form.transportation_type==='none'} />
          </div>
        </div>

        <div style={st.grid2}>
          <div>
            <label style={st.label}>Insurance Type</label>
            <select value={form.insurance_type} onChange={e => setForm({...form,insurance_type:e.target.value})} style={st.input}>
              <option value="none">None</option>
              <option value="actual">Actual</option>
              <option value="manual">Manual</option>
            </select>
          </div>
          <div>
            <label style={st.label}>Insurance Amount</label>
            <input type="number" step="0.01" value={form.insurance_amount} onChange={e => setForm({...form,insurance_amount:e.target.value})} style={st.input} disabled={form.insurance_type!=='manual'} />
          </div>
        </div>

        <label style={st.label}>Notes</label>
        <textarea value={form.notes} onChange={e => setForm({...form,notes:e.target.value})} style={{...st.input,minHeight:50}} />

        <button onClick={saveAssignment} style={{...st.btnPrimary,marginTop:8}}>{selected ? 'Save' : 'Create'}</button>
      </div>
    </div>
  ) : null;

  // ── LIST VIEW ──
  return (
    <div style={st.page}>
      <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',flexWrap:'wrap',gap:12,marginBottom:16}}>
        <h2 style={st.title}>Employee Assignments</h2>
        <button onClick={openCreateForm} style={st.btnPrimary}>+ New Assignment</button>
      </div>

      <div style={st.filterRow}>
        <input placeholder="Search employee..." value={search} onChange={e => setSearch(e.target.value)} onKeyDown={e => e.key==='Enter' && fetchAssignments()} style={{...st.input,maxWidth:220}} />
        <select value={clientFilter} onChange={e => { setClientFilter(e.target.value); setPage(1); }} style={{...st.input,width:180}}>
          <option value="">All Clients</option>
          {clients.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPage(1); }} style={{...st.input,width:140}}>
          <option value="">All Statuses</option>
          {['draft','active','on_hold','ended','archived'].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <button onClick={() => { setPage(1); fetchAssignments(); }} style={st.btnPrimary}>Search</button>
      </div>

      {initialLoading ? <p>Loading...</p> : assignments.length === 0 ? <p style={{color:'#999'}}>No assignments found.</p> : (
        <div style={st.tableWrap}>
          <table style={st.table}>
            <thead>
              <tr>
                <th style={st.th}>Employee</th>
                <th style={st.th}>Client</th>
                <th style={st.th}>Service</th>
                <th style={st.th}>Status</th>
                <th style={st.th}>Salary</th>
                <th style={st.th}>Rate</th>
                <th style={st.th}>Start</th>
                <th style={st.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {assignments.map((a, i) => (
                <tr key={a.id} style={i%2===0 ? st.rowEven : st.rowOdd}>
                  <td style={st.td}><span style={{cursor:'pointer',color:'#2980b9',fontWeight:600}} onClick={() => openAssignment(a)}>{a.employee?.name || `#${a.employee_id}`}</span></td>
                  <td style={st.td}>{a.client?.name || '—'}</td>
                  <td style={st.td}>{a.service?.name || '—'}</td>
                  <td style={st.td}><span style={{...st.badge,backgroundColor:statusClr(a.status)}}>{a.status}</span></td>
                  <td style={st.td}>${Number(a.outsourcing_salary||0).toLocaleString()}</td>
                  <td style={st.td}>${Number(a.billing_rate||0).toLocaleString()}</td>
                  <td style={st.td}>{a.start_date || '—'}</td>
                  <td style={st.td}><button onClick={() => openAssignment(a)} style={st.btnSm}>View</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {lastPage > 1 && (
        <div style={st.pagination}>
          <button disabled={page<=1} onClick={() => setPage(page-1)} style={st.pageBtn}>Prev</button>
          <span>Page {page} of {lastPage}</span>
          <button disabled={page>=lastPage} onClick={() => setPage(page+1)} style={st.pageBtn}>Next</button>
        </div>
      )}

      {formModal}
    </div>
  );
}

function statusClr(s) {
  const m = { draft:'#95a5a6', active:'#27ae60', on_hold:'#f39c12', ended:'#e74c3c', archived:'#7f8c8d' };
  return m[s] || '#95a5a6';
}

const st = {
  page: { padding:24, maxWidth:1200, margin:'0 auto' },
  title: { fontSize:24, fontWeight:700, margin:0 },
  filterRow: { display:'flex', gap:10, marginBottom:16, flexWrap:'wrap' },
  input: { padding:'8px 12px', borderRadius:6, border:'1px solid #ccc', fontSize:14, width:'100%', boxSizing:'border-box', marginBottom:8 },
  label: { display:'block', fontSize:13, fontWeight:600, marginBottom:4, marginTop:8 },
  tableWrap: { overflowX:'auto' },
  table: { width:'100%', borderCollapse:'collapse' },
  th: { textAlign:'left', padding:'10px 12px', borderBottom:'2px solid #3498db', fontSize:13, fontWeight:600, whiteSpace:'nowrap' },
  td: { padding:'10px 12px', borderBottom:'1px solid #eee', fontSize:14 },
  rowEven: { background:'#fafafa' },
  rowOdd: { background:'#fff' },
  badge: { display:'inline-block', padding:'3px 10px', borderRadius:12, color:'#fff', fontSize:12, fontWeight:600, textTransform:'capitalize' },
  btnPrimary: { padding:'8px 16px', background:'#3498db', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:14, fontWeight:600 },
  btnWarn: { padding:'8px 16px', background:'#f39c12', color:'#fff', border:'none', borderRadius:6, cursor:'pointer', fontSize:14 },
  btnSm: { padding:'4px 10px', background:'#ecf0f1', border:'none', borderRadius:4, cursor:'pointer', fontSize:13 },
  backBtn: { background:'none', border:'none', color:'#3498db', cursor:'pointer', fontSize:14, fontWeight:600, marginBottom:12, padding:0 },
  detailHeader: { display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12, marginBottom:16 },
  section: { background:'#fff', borderRadius:10, padding:20, boxShadow:'0 2px 8px rgba(0,0,0,0.08)', marginBottom:16 },
  grid2: { display:'grid', gridTemplateColumns:'1fr 1fr', gap:'8px 16px' },
  overlay: { position:'fixed', top:0, left:0, right:0, bottom:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 },
  modal: { background:'#fff', borderRadius:12, padding:24, width:'90%', maxWidth:600, maxHeight:'85vh', overflowY:'auto' },
  pagination: { display:'flex', justifyContent:'center', alignItems:'center', gap:16, marginTop:16 },
  pageBtn: { padding:'6px 14px', border:'1px solid #ccc', borderRadius:6, background:'#fff', cursor:'pointer' },
};
